import BackgroundColors from '@root/joinroot.com/src/utils/background-colors';
import BodyCopy from '@root/joinroot.com/src/components/body-copy';
import BrandBling, { BRAND_BLING_OPTIONS } from '@root/joinroot.com/src/components/brand-bling';
import Img from '@root/website-tooling/gatsby-image';
import LinkButton from '@root/joinroot.com/src/components/buttons/link-button';
import PropTypes from '@root/vendor/prop-types';
import { Eyebrow, H2 } from '@root/joinroot.com/src/components/text';
import { StyleSheet } from '@root/core/src/utils/styles';

export const IMAGE_HORIZONTAL_ALIGNMENTS = {
  LEFT: 'left',
  RIGHT: 'right',
};

export const IMAGE_VERTICAL_ALIGNMENTS = {
  TOP: 'top',
  CENTER: 'center',
};

export const IMAGE_SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
};

export default function ImageToutSideBySide({
  backgroundColor = BackgroundColors.cmsValues.WHITE,
  body,
  brandBlingOptions = BRAND_BLING_OPTIONS.NONE,
  button,
  trackClick,
  eyebrow,
  image,
  imageAltText,
  imageHorizontalAlignment = IMAGE_HORIZONTAL_ALIGNMENTS.RIGHT,
  imageFullBleed = false,
  imageSize = IMAGE_SIZES.MEDIUM,
  title,
  imageVerticalAlignment = IMAGE_VERTICAL_ALIGNMENTS.CENTER,
  buttonComponent = LinkButton,
}) {
  const ButtonComponent = buttonComponent;

  const sectionClasses = [`image-${imageHorizontalAlignment}`, `image-size-${imageSize}`];
  const containerClasses = [];

  if (imageFullBleed) {
    sectionClasses.push('image-fullbleed');
  }

  if (imageFullBleed && brandBlingOptions === BRAND_BLING_OPTIONS.CARET) {
    containerClasses.push('with-caret-overlay');
  }

  if (imageFullBleed && brandBlingOptions === BRAND_BLING_OPTIONS.WORDMARK) {
    containerClasses.push('with-root-overlay');
  }

  return (
    <section
      className={sectionClasses.join(' ')}
      css={[
        styles.section,
        BackgroundColors.getBackgroundStyle(backgroundColor),
      ]}
    >
      <div
        className={containerClasses.join(' ')}
        css={[styles.innerContainer, imageVerticalAlignmentStyles[imageVerticalAlignment]]}
      >
        <div css={styles.textContainer}>
          {eyebrow && <Eyebrow css={styles.eyebrow}>{eyebrow}</Eyebrow>}
          <H2 css={styles.title}>{title}</H2>
          <BodyCopy
            content={body}
            css={styles.body}
          />
          {button &&
            <ButtonComponent
              buttonStyles={styles.button}
              onClick={() => trackClick(`CTA_${button.id}`)}
              to={button.link}
              type={button.type}
            >
              {button.text}
            </ButtonComponent>
          }
        </div>
        <div css={styles.imageContainer}>
          {imageFullBleed &&
            <BrandBling
              brandBlingOptions={brandBlingOptions}
              brandBlingStyles={styles[brandBlingOptions]}
            />
          }
          <Img
            alt={imageAltText ?? ''}
            css={styles.image}
            fluid={image.fluid}
          />
        </div>
      </div>
    </section>
  );
}

const styles = StyleSheet.create({
  section: {
    '&.image-fullbleed': {
      paddingBottom: 0,
    },
    '@media screen and (min-width: 768px)': {
      padding: '50px 60px',
      '&.image-fullbleed': {
        padding: '50px 20px',
      },
    },
    '@media screen and (min-width: 1280px)': {
      padding: '100px 120px',
      '& + &': {
        padding: '0 120px 100px',
      },
      '&.image-fullbleed': {
        padding: 0,
      },
    },
  },
  innerContainer: {
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'center',
    '@media screen and (min-width: 768px)': {
      justifyContent: 'space-between',
      maxWidth: 1160,
      margin: 'auto',
      '.image-right &': {
        flexDirection: 'row',
      },
      '.image-left &': {
        flexDirection: 'row-reverse',
      },
    },
    '@media screen and (min-width: 1280px)': {
      '.image-fullbleed &': {
        maxWidth: 'none',
        justifyContent: 'flex-end',
      },
    },
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
    marginBottom: 35,
    '.with-caret-overlay &': {
      marginBottom: 45,
    },
    '.with-root-overlay &': {
      overflow: 'hidden',
    },
    '@media screen and (min-width: 768px)': {
      maxWidth: 560,
      marginBottom: 0,
    },
    '@media screen and (min-width: 1280px)': {
      '.image-size-small &': {
        maxWidth: 480,
      },
      '.image-size-medium &': {
        maxWidth: 700,
      },
      '.image-fullbleed &': {
        maxWidth: 'none',
        width: '58.33%',
      },
      '.with-caret-overlay &': {
        marginBottom: 0,
      },
    },
  },
  caret: {
    display: 'block',
    zIndex: 2,
    left: 0,
    right: 0,
    bottom: -23,
    width: 93,
    margin: 'auto',
    '@media screen and (min-width: 768px)': {
      width: 93,
      top: 0,
      bottom: 0,
      '.image-right &': {
        left: -47,
        right: 'auto',
      },
      '.image-left &': {
        right: -47,
        left: 'auto',
      },
    },
    '@media screen and (min-width: 1280px)': {
      width: 152,
      '.image-right &': {
        left: -76,
      },
      '.image-left &': {
        right: -76,
      },
    },
  },
  wordmark: {
    display: 'block',
    zIndex: 2,
    bottom: -60,
    left: -90,
    minWidth: 1100,
    maxWidth: 1800,
    width: '250vw',
    height: 'auto',
    margin: 0,
    '@media screen and (min-width: 768px)': {
      left: -205,
      bottom: -145,
      maxWidth: 1400,
      height: 'auto',
    },
    '@media screen and (min-width: 1280px)': {
      minWidth: 2250,
      maxWidth: 3000,
      width: '150vw',
      left: -210,
      bottom: -175,
      height: 'auto',
    },
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingLeft: 20,
    paddingRight: 20,
    width: '100%',
    maxWidth: 450,
    textAlign: 'center',
    '@media screen and (min-width: 768px)': {
      maxWidth: 560,
      alignItems: 'flex-start',
      textAlign: 'left',
      '.image-right &': {
        paddingRight: 40,
        marginRight: 35,
      },
      '.image-left &': {
        paddingLeft: 40,
        marginLeft: 35,
      },
    },
    '@media screen and (min-width: 1280px)': {
      padding: 0,
      maxWidth: 340,
      '.image-right &': {
        paddingRight: 0,
      },
      '.image-left &': {
        paddingLeft: 0,
      },
      '.image-size-small &': {
        maxWidth: 580,
      },
      '.image-size-medium &': {
        maxWidth: 360,
      },
      '.image-fullbleed &': {
        maxWidth: 360,
      },
      '.image-fullbleed.image-right &': {
        marginRight: 140,
        marginLeft: 35,
      },
      '.image-fullbleed.image-left &': {
        marginLeft: 140,
        marginRight: 35,
      },
    },
    '@media screen and (min-width: 1600px)': {
      '.image-right &': {
        marginRight: 0,
      },
      '.image-left &': {
        marginLeft: 0,
      },
    },
  },
  eyebrow: {
    marginBottom: 15,
  },
  title: {
    marginTop: 0,
    marginBottom: 15,
  },
  body: {
    marginBottom: 35,
  },
  button: {
    marginBottom: 50,
    '@media screen and (min-width: 768px)': {
      marginBottom: 0,
    },
  },
});

const imageVerticalAlignmentStyles = StyleSheet.create({
  center: {
    '@media screen and (min-width: 768px)': {
      alignItems: 'center',
    },
  },
  top: {
    '@media screen and (min-width: 768px)': {
      alignItems: 'flex-start',
    },
  },
});

ImageToutSideBySide.propTypes = {
  backgroundColor: PropTypes.oneOf(BackgroundColors.cmsValueList),
  body: PropTypes.node,
  brandBlingOptions: PropTypes.oneOf([BRAND_BLING_OPTIONS.NONE, BRAND_BLING_OPTIONS.CARET, BRAND_BLING_OPTIONS.WORDMARK]),
  button: PropTypes.shape({
    id: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  buttonComponent: PropTypes.func,
  eyebrow: PropTypes.node,
  image: PropTypes.object.isRequired,
  imageAltText: PropTypes.string.isRequired,
  imageFullBleed: PropTypes.bool,
  imageHorizontalAlignment: PropTypes.oneOf(Object.values(IMAGE_HORIZONTAL_ALIGNMENTS)),
  imageSize: PropTypes.oneOf(Object.values(IMAGE_SIZES)),
  imageVerticalAlignment: PropTypes.oneOf(Object.values(IMAGE_VERTICAL_ALIGNMENTS)),
  title: PropTypes.node,
  trackClick: PropTypes.func.isRequired,
};
